<script setup>
import * as v from 'valibot'
import { vMaska } from "maska/vue"

const { $bus } = useNuxtApp();
const route = useRoute()
const { fetch: refreshSession } = useUserSession()

const props = defineProps({
    copy: {
        type: String,
        default: 'You must log in using the email you registered with and your ticket confirmation number.'
    },
})

// `open` is forwarded straight through from the parent FragmentsDialogsLogin
// (which itself is bound to FragmentsModalShell's overlay-compatible model).
// Setting it false here bubbles all the way up to ModalShell's `close` emit,
// resolving the useModal() promise and letting the deep-link bridge strip
// the URL — no query manipulation happens in this component anymore.
const open = defineModel('open')

const auth_form = useTemplateRef('auth_form')
const busy = ref(false)

const schema = v.object({
    email: v.pipe(v.string(), v.trim(), v.email('Please enter a valid email address.')),
    ticketId: v.pipe(v.string(), v.trim(), v.nonEmpty('Please enter your ticket number.'))
})

const state = reactive({
    email: '',
    ticketId: ''
})

// The credentials were rejected. Both fields take the error stroke, but the
// message stays generic and is shown once — we deliberately never reveal which
// of the two was wrong. Email and ticket are judged as a pair, so editing
// either one clears the whole auth error rather than leaving the other field
// stranded in a red state.
const authFailed = ref(false)
watch(state, () => {
    if (!authFailed.value) return
    authFailed.value = false
    auth_form.value?.setErrors([])
})

const labelStyles = {
    root: 'w-full',
    label: 'w-full text-left',
    container: 'w-full mt-[8px]',
    error: 'mt-[6px] text-[13px] font-medium text-[#980000] text-left w-full',
    help: '',
}

const inputUi = (error) => ({
    root: 'w-full',
    base: [
        'w-full text-left bg-white rounded-[50px] py-[21px] px-[26px] text-[15px]',
        'font-hubspot-sans font-light text-[var(--magenta)]',
        'placeholder:text-[15px] placeholder:text-[#a55c87] focus:outline-none border',
        (error || authFailed.value) ? 'border-[#980000]' : 'border-[#a55c87]',
    ].join(' '),
})

const onSubmit = async (e) => {
    busy.value = true
    authFailed.value = false

    try {
        const { response } = await $fetch('/api/v1/user/registration', { method: 'POST', body: { email: state.email, ticketId: state.ticketId } } )
        await refreshSession()
        // Captured BEFORE the navigation below, because that navigation is what
        // destroys the evidence: braindate and schedule-builder leave for their
        // own routes and drop `action` from the query entirely. Listeners run
        // after it, so by then there is no way to tell a login that was resuming
        // a task from one that started cold.
        const resumedAction = route.query.action ?? null
        const { modal, ...newQuery } = route.query
        // Resume the action the visitor was originally reaching for — the middleware
        // sent them here instead of it, so hand them back to it now they're in.
        // Hand the visitor back to the specific braindate they clicked, not the root.
        // safeBraindateTarget on the other side rejects anything off-origin.
        if (route.query.action === 'braindate') {
            await navigateTo({ path: '/braindate', query: route.query.to ? { to: route.query.to } : {} })
        }
        else if (route.query.action === 'schedule-builder') await navigateTo({ path: '/build-schedule' })
        else await navigateTo({ path: route.path, query: newQuery })
        open.value = false
        // The bus is mitt-shaped: emit(type, payload) carries exactly ONE
        // payload, so the action travels inside it. Every existing listener
        // ignores the payload and only refetches, so widening it is safe.
        $bus.emit('auth:login', { response, action: resumedAction })
    } catch (error) {
        authFailed.value = true
        auth_form.value.setErrors([
            { name: 'email', message: 'Incorrect email or ticket number'  }
        ])
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div class="login-form">
        <p class="login-form__copy">
            {{ copy }}
            If you haven't registered already, you can <a href="/register" class="login-form__link" @click.prevent="open = false; nextTick(() => navigateTo('/register'))">purchase registration here</a>.
        </p>

        <UForm :schema="schema" :state="state" @submit="onSubmit" ref="auth_form" class="login-form__form">
            <UFormField name="email" :ui="labelStyles" :class="{ 'blur-xs': busy }">
                <template #label>
                    <span class="login-form__label">Email:<span class="login-form__label-star">*</span></span>
                </template>
                <template #default="{ error }">
                    <UInput v-model="state.email" variant="none" :ui="inputUi(error)" placeholder="example@hubspot.com" />
                </template>
            </UFormField>
            <UFormField name="ticketId" :ui="labelStyles" :class="{ 'blur-xs': busy }">
                <template #label>
                    <span class="login-form__label">Ticket Number:<span class="login-form__label-star">*</span></span>
                </template>
                <template #help>
                    <span class="login-form__ticket-help">Your ticket number can be found in your confirmation email.</span>
                </template>
                <template #default="{ error }">
                    <UInput v-model="state.ticketId" type="text" variant="none" :ui="inputUi(error)" v-maska="'########'" placeholder="12345678" />
                </template>
            </UFormField>
            <button type="submit" class="login-form__submit" :disabled="busy">
                Login
            </button>
        </UForm>

        <p class="login-form__help-text">
            Having problems logging in? Visit our <a href="/help" class="login-form__help-link" @click.prevent="open = false; nextTick(() => navigateTo('/help'))">help page</a>.
        </p>

        <button type="button" class="login-form__close" @click="open = false">
            Close
        </button>
    </div>
</template>

<style>
.login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 38.875rem;
    gap: 30px;
}

.login-form__copy {
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 15px;
    line-height: 23px;
    letter-spacing: 0.3px;
    color: var(--magenta);
    text-align: center;
}

.login-form__link {
    color: var(--magenta);
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: #7d4566;
    text-underline-offset: 3px;
}

.login-form__link:hover {
    color: var(--purple);
    text-decoration: none;
}

.login-form__form {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
}

.login-form__label {
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 13px;
    letter-spacing: 0.26px;
    color: var(--magenta);
}

/* Magenta, not the error red — red is reserved for validation errors. */
.login-form__label-star {
    color: var(--magenta);
}

.login-form__ticket-help {
    display: block;
    width: 100%;
    text-align: left;
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 12px;
    letter-spacing: 0.24px;
    color: #7d4566;
}

.login-form__submit {
    font-family: var(--font-hubspot-serif);
    font-weight: 400;
    font-size: 17px;
    line-height: 1.4;
    letter-spacing: 0.34px;
    color: var(--magenta);
    background: transparent;
    border: 1px solid var(--magenta);
    border-radius: 50px;
    padding: 10px 20px;
    /* 45px of separation from the fields above. The form's flex gap already
       contributes 20px, so this tops it up rather than setting the full value. */
    margin-top: 25px;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.login-form__submit:hover {
    background: var(--magenta);
    color: #fff;
}

.login-form__submit:disabled {
    opacity: 0.6;
    cursor: default;
}

.login-form__help-text {
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 14px;
    color: var(--magenta);
    text-align: center;
}

.login-form__help-link {
    color: var(--magenta);
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: #7d4566;
    text-underline-offset: 3px;
}

.login-form__help-link:hover {
    color: var(--purple);
    text-decoration: none;
}

.login-form__close {
    font-family: var(--font-hubspot-sans);
    font-weight: 300;
    font-size: 14px;
    color: #7d4566;
    background: none;
    border: none;
    padding: 0;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 3px;
    cursor: pointer;
}

.login-form__close:hover {
    color: var(--purple);
    text-decoration: none;
}
</style>
